import React from "react";
import styles from "./PerformanceHub.module.css";
import { aiCallAgentPerformanceData as data } from "../../data/ai-call-agent";

export default function PerformanceHub({ dynamicData }) {
  // Requirement 4: Use dynamicData if available, otherwise fallback to static data
  const finalData = dynamicData || data;

  const fmtMoney = (n) =>
    new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(n);

  return (
    <section className="py-5">
      <div className="container">
        <div className="text-center mb-4" data-reveal="fade">
          <span className={`text-uppercase small ${styles.eyebrow}`}>
            Performance & Intelligence Hub
          </span>
          <h2 className={`mt-2 fw-bold ${styles.title}`}>
            Our platform provides more than just automation; it delivers
            transparent, actionable insights in real-time.
          </h2>
          <p className={`mt-2 ${styles.sub}`}>
            Below is a snapshot of the typical performance metrics our clients
            achieve, showcasing the intelligence driving every call.
          </p>
        </div>

        <div className="row g-4" data-reveal="stagger-up">
          <div className="col-12">
            <h5 className={`mb-2 ${styles.title}`}>
              Operational Efficiency Metrics
            </h5>
            <p className={`mb-3 ${styles.sub}`}>
              This dashboard highlights the core improvements in speed,
              resolution, and call management.
            </p>
          </div>

          <div className="col-12 col-md-6 col-lg-4">
            <div className={styles.card}>
              <div className={styles.label}>Average Handle Time (AHT)</div>
              <div className="d-flex align-items-end justify-content-between mt-1">
                <div>
                  <div className={styles.metric}>
                    {finalData.efficiency.aht.from} → {finalData.efficiency.aht.to}
                  </div>
                  <div className={styles.sub}>Live Agent → AI Agent</div>
                </div>
                <span className={styles.badge}>
                  <span className={styles.dot} />
                  {finalData.efficiency.aht.badge}
                </span>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6 col-lg-4">
            <div className={styles.card}>
              <div className={styles.label}>First Call Resolution (FCR)</div>
              <div className={`mt-1 ${styles.metric}`}>
                {finalData.efficiency.fcr}%
              </div>
              <div className={`mt-2 ${styles.barWrap}`}>
                <div
                  className={styles.bar}
                  style={{ "--bar-width": `${finalData.efficiency.fcr}%` }}
                ></div>
              </div>
              <div className={`mt-1 ${styles.sub}`}>
                Issue resolved on first attempt without escalation.
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6 col-lg-4">
            <div className={styles.card}>
              <div className={styles.label}>Call Containment Rate</div>
              <div className={`mt-1 ${styles.metric}`}>
                {finalData.efficiency.containment}%
              </div>
              <div className={`mt-2 ${styles.barWrap}`}>
                <div
                  className={styles.bar}
                  style={{
                    "--bar-width": `${finalData.efficiency.containment}%`,
                    "--bar-color": "#58c28e",
                  }}
                ></div>
              </div>
              <div className={`mt-1 ${styles.sub}`}>
                Inbound calls fully handled by AI end‑to‑end.
              </div>
            </div>
          </div>
        </div>

        <div className="row g-4 mt-1" data-reveal="stagger-up">
          <div className="col-12">
            <h5 className={`mb-2 ${styles.title}`}>Financial Impact & ROI</h5>
          </div>

          <div className="col-12 col-md-6">
            <div className={styles.card}>
              <div className={styles.label}>Cost Per Interaction</div>
              <div className="d-flex align-items-end justify-content-between mt-1">
                <div>
                  <div className={styles.metric}>
                    ${finalData.finance.costPerInteraction.live.toFixed(2)} vs $
                    {finalData.finance.costPerInteraction.ai.toFixed(2)}
                  </div>
                  <div className={styles.sub}>Avg. Live Agent vs AI Agent</div>
                </div>
                <span className={styles.badge}>
                  <span className={styles.dot} />
                  {finalData.finance.costPerInteraction.badge}
                </span>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6">
            <div className={styles.card}>
              <div className={styles.label}>Monthly Operational Savings</div>
              <div className={`mt-1 ${styles.metric}`}>
                {fmtMoney(finalData.finance.monthlySavings)}
              </div>
              <div className={`mt-1 ${styles.sub}`}>
                {finalData.finance.baseVolume}
              </div>
            </div>
          </div>
        </div>

        <div className="row g-4 mt-1" data-reveal="stagger-up">
          <div className="col-12">
            <h5 className={`mb-2 ${styles.title}`}>
              Lead Qualification & Conversion
            </h5>
          </div>
          <div className="col-12 col-md-6 col-lg-4">
            <div className={styles.card}>
              <div className={styles.label}>Lead Qualification Rate</div>
              <div className={`mt-1 ${styles.metric}`}>
                {finalData.conversion.leadQualification}%
              </div>
              <div className={`mt-2 ${styles.barWrap}`}>
                <div
                  className={styles.bar}
                  style={{
                    "--bar-width": `${finalData.conversion.leadQualification}%`,
                    "--bar-color": "#5fb2ff",
                  }}
                ></div>
              </div>
              <div className={`mt-1 ${styles.sub}`}>
                AI identifies and qualifies high‑intent leads.
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6 col-lg-4">
            <div className={styles.card}>
              <div className={styles.label}>Customer Satisfaction (CSAT)</div>
              <div className={`mt-1 ${styles.metric}`}>
                {finalData.conversion.csat}
              </div>
              <div className={`mt-1 ${styles.sub}`}>
                From post‑call surveys.
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6 col-lg-4">
            <div className={styles.card}>
              <div className={styles.label}>Live Sentiment Analysis</div>
              <div className="d-flex align-items-center justify-content-between mt-1">
                <span className={styles.sentimentPositive}>
                  Positive: {finalData.conversion.sentiment.positive}%
                </span>
                <span className={styles.sentimentNeutral}>
                  Neutral: {finalData.conversion.sentiment.neutral}%
                </span>
                <span className={styles.sentimentNegative}>
                  Negative: {finalData.conversion.sentiment.negative}%
                </span>
              </div>
              <div className={`mt-2 ${styles.barWrap}`}>
                <div style={{ display: "flex", height: "100%", width: "100%" }}>
                  <div
                    className={styles.bar}
                    style={{
                      "--bar-width": `${finalData.conversion.sentiment.positive}%`,
                      "--bar-color": "#58c28e",
                    }}
                  />
                  <div
                    className={styles.bar}
                    style={{
                      "--bar-width": `${finalData.conversion.sentiment.neutral}%`,
                      "--bar-color": "#e1a21f",
                    }}
                  />
                  <div
                    className={styles.bar}
                    style={{
                      "--bar-width": `${finalData.conversion.sentiment.negative}%`,
                      "--bar-color": "#e06565",
                    }}
                  />
                </div>
              </div>
              <div className={`mt-1 ${styles.sub}`}>
                Real‑time escalation to live agent on frustration spikes.
              </div>
            </div>
          </div>
        </div>

        <div className="row g-4 mt-1" data-reveal="stagger-up">
          <div className="col-12 col-lg-7">
            <div className={styles.card}>
              <div className={styles.label}>
                Top 5 Customer Intents Identified
              </div>
              <ul
                className="mt-2 mb-0"
                style={{
                  listStyle: "none",
                  padding: 0,
                  margin: 0,
                  color: "#fff",
                }}
              >
                {finalData.conversion.topIntents.map((it) => (
                  <li
                    key={it.name}
                    className="d-flex align-items-center justify-content-between py-1"
                  >
                    <span>{it.name}</span>
                    <span>{it.pct}%</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="col-12 col-lg-5">
            <div className={styles.card}>
              <div className={styles.label}>Why it matters</div>
              <p className={`mt-2 mb-2 ${styles.sub}`}>
                Insights help shape better processes, triage flows, and
                knowledge base improvements. The result is fewer escalations and
                faster outcomes.
              </p>
              {finalData.conversion.whyMatters.map((w, i) => (
                <div
                  key={`${w.label}-${i}`}
                  className="d-flex align-items-center gap-2 mt-2"
                >
                  <span
                    className={styles.dot}
                    style={{ "--dot-color": w.color }}
                  />
                  {w.label}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
